import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { AppServices } from '../../servicelayer/app-services';
import { appRouteConstants } from '../../app.route.constants';
import { InitializationStatusCodes } from 'portalServices';
import { RouteAuthenticationService } from './route-auth.service';

@Injectable({
    providedIn: 'root'
})
export class AppInitializationService {
    private initCompleted = new BehaviorSubject<boolean>(false);
    private lastSuccessfulRoute = '';
    private autoRedirectDisabled = false;

    constructor(
        private appServices: AppServices,
        private router: Router,
        private routeAuthService: RouteAuthenticationService
    ) {
        this.initializeApp();
        this.trackSuccessfulNavigations();
    }

    /**
     * Returns the current URL. During app bootstrap (constructors / DI),
     * `Router.url` is still '/' because the initial navigation hasn't been
     * parsed yet. Reading from `window.location` gives the real deep-link
     * URL (e.g. /join/joining-form?token=...).
     */
    private getCurrentUrl(): string {
        if (typeof window !== 'undefined' && window.location) {
            return `${window.location.pathname}${window.location.search}`;
        }
        return this.router.url;
    }

    public get authenticationService() {
        // Don't restrict access for login routes - only for join routes
        const currentUrl = this.getCurrentUrl();
        if (currentUrl.includes('join')) {
            console.warn('AppInitializationService: Attempted to access authenticationService on join route');
            return null;
        }
        return this.appServices.authenticationService;
    }

    public get initializationService() {
        // Don't restrict access for login routes - only for join routes  
        const currentUrl = this.getCurrentUrl();
        if (currentUrl.includes('join')) {
            console.warn('AppInitializationService: Attempted to access initializationService on join route');
            return null;
        }
        return this.appServices.initializationService;
    }

    private initializeApp(): void {
        const currentUrl = this.getCurrentUrl();
        console.log('AppInitializationService: Initializing app for URL:', currentUrl);
        
        // For joining form routes, we don't need to wait for full authentication
        if (currentUrl.includes('join')) {
            console.log('AppInitializationService: Joining form route detected - skipping authentication-dependent initialization');
            this.initCompleted.next(true);
            return;
        }
        
        // For login routes, skip the automatic initialization that triggers getProfile
        if (currentUrl.includes('login')) {
            console.log('AppInitializationService: Login route detected - skipping automatic authentication initialization to avoid getProfile calls');
            this.initCompleted.next(true);
            return;
        }
        
        // Wait for initialization to be ready - but don't handle routing here
        // Let the authentication guard handle routing decisions
        combineLatest([
            this.initializationService.initState,
            this.routeAuthService.isAuthenticated$
        ]).pipe(
            filter(([initState]) => 
                initState === InitializationStatusCodes.RUNNING || 
                initState === InitializationStatusCodes.UNAUTHENTICATED
            ),
            take(1)
        ).subscribe(([initState, isAuthenticated]) => {
            console.log('AppInitializationService: App initialization complete');
            console.log('Init State:', initState);
            console.log('Is Authenticated:', isAuthenticated);
            console.log('Current URL:', this.router.url);
            
            this.initCompleted.next(true);
            
            // Only handle routing for root routes and only if auto-redirect is not disabled
            if (!this.autoRedirectDisabled) {
                setTimeout(() => this.handlePostInitializationRouting(), 200);
            }
        });
    }

    private handlePostInitializationRouting(): void {
        const currentUrl = this.router.url;
        console.log('AppInitializationService: Checking post-init routing for URL:', currentUrl);
        console.log('AppInitializationService: Is root route?', this.isRootRoute(currentUrl));
        console.log('AppInitializationService: Auto-redirect disabled?', this.autoRedirectDisabled);

        // Check authentication service availability
        const authService = this.authenticationService;
        if (!authService) {
            console.warn('AppInitializationService: Authentication service is null, cannot check authentication status');
            return;
        }

        console.log('AppInitializationService: Authentication service available, checking auth status...');
        const isAuthenticated = authService.isAuthenticated();
        console.log('AppInitializationService: Is user authenticated?', isAuthenticated);

        // Only redirect if:
        // 1. We're on a root route
        // 2. User is authenticated
        // 3. Auto-redirect is not disabled
        // 4. We're not already on a specific route
        if (this.isRootRoute(currentUrl) && 
            isAuthenticated && 
            !this.autoRedirectDisabled) {
            
            console.log('AppInitializationService: All conditions met - redirecting authenticated user from root to dashboard');
            console.log('AppInitializationService: Target route:', appRouteConstants.DASHBOARD);
            
            this.router.navigate([appRouteConstants.DASHBOARD]).then(success => {
                if (success) {
                    console.log('AppInitializationService: Successfully redirected to dashboard');
                } else {
                    console.error('AppInitializationService: Failed to redirect to dashboard');
                }
            }).catch(error => {
                console.error('AppInitializationService: Error during dashboard redirect:', error);
            });
        } else {
            console.log('AppInitializationService: Redirect conditions not met:', {
                isRootRoute: this.isRootRoute(currentUrl),
                isAuthenticated: isAuthenticated,
                autoRedirectDisabled: this.autoRedirectDisabled
            });
        }
    }

    private trackSuccessfulNavigations(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            tap(event => {
                const navigationEnd = event as NavigationEnd;
                console.log('AppInitializationService: Navigation completed to:', navigationEnd.url);
                
                // Re-enable auto-redirect if user navigates to root or auth routes
                if (this.shouldAllowAutoRedirect(navigationEnd.url)) {
                    if (this.autoRedirectDisabled) {
                        console.log('AppInitializationService: Re-enabling auto-redirect for route:', navigationEnd.url);
                        this.enableAutoRedirect();
                    }
                }
                
                // If this is not a root route and not an auth route, record it
                if (!this.isRootRoute(navigationEnd.url) && !this.isAuthRoute(navigationEnd.url)) {
                    this.lastSuccessfulRoute = navigationEnd.url;
                    console.log('AppInitializationService: Tracked successful route:', navigationEnd.url);
                }
            })
        ).subscribe();
    }

    /**
     * Get the initialization status
     */
    isInitialized(): Observable<boolean> {
        return this.initCompleted.asObservable();
    }

    private isLoginRoute(url: string): boolean {
        return url.includes(appRouteConstants.AUTH.LOGIN);
    }

    private isJoinRoute(url: string): boolean {
        return url.includes('join');
    }

    private isAuthRoute(url: string): boolean {
        return url.includes(appRouteConstants.AUTH.LOGIN) ||
               url.includes(appRouteConstants.AUTH.RESET) ||
               url.includes(appRouteConstants.AUTH.RESET_REQUEST) ||
               url.includes('join');
    }

    private isRootRoute(url: string): boolean {
        const cleanUrl = url.split('?')[0].toLowerCase(); // Remove query parameters
        return ['/', '', '/hr', '/hr/', 'hr', 'hr/', '/hr/dashboard'].includes(cleanUrl);
    }

    /**
     * Get the last successful route for recovery after login
     */
    getLastSuccessfulRoute(): string {
        return this.lastSuccessfulRoute || appRouteConstants.DASHBOARD;
    }

    /**
     * Disable auto-redirect (called by authentication guard when user accesses specific routes)
     */
    disableAutoRedirect(): void {
        this.autoRedirectDisabled = true;
        console.log('AppInitializationService: Auto-redirect disabled');
    }

    /**
     * Check if auto-redirect is disabled
     */
    isAutoRedirectDisabled(): boolean {
        return this.autoRedirectDisabled;
    }

    /**
     * Re-enable auto-redirect (useful for login scenarios and root routes)
     */
    enableAutoRedirect(): void {
        this.autoRedirectDisabled = false;
        console.log('AppInitializationService: Auto-redirect re-enabled');
    }

    /**
     * Check if the current route should allow auto-redirect
     */
    shouldAllowAutoRedirect(url: string): boolean {
        return this.isRootRoute(url) || this.isLoginRoute(url) || this.isAuthRoute(url);
    }

    /**
     * Handle session timeout (401 errors)
     */
    handleSessionTimeout(): void {
        const currentUrl = this.router.url;
        console.log('AppInitializationService: Session timeout requested for URL:', currentUrl);
        
        // Don't handle session timeout for joining form routes
        if (currentUrl.includes('join')) {
            console.log('AppInitializationService: Ignoring session timeout for joining form route');
            return;
        }
        
        console.log('AppInitializationService: Session timed out, redirecting to login');
        
        // Only set returnUrl if it's not a root route - otherwise let it default to dashboard
        let returnUrl = '';
        if (!this.isRootRoute(currentUrl) && !this.isAuthRoute(currentUrl)) {
            returnUrl = currentUrl;
        }
        
        // Reset state
        this.autoRedirectDisabled = false;
        this.lastSuccessfulRoute = returnUrl || appRouteConstants.DASHBOARD;
        
        // Navigate to login with appropriate parameters
        if (returnUrl) {
            this.router.navigate([appRouteConstants.AUTH.LOGIN], {
                queryParams: { sessionTimeout: 'true', returnUrl: returnUrl }
            });
        } else {
            // Don't add returnUrl for root routes to avoid the %2F issue
            this.router.navigate([appRouteConstants.AUTH.LOGIN], {
                queryParams: { sessionTimeout: 'true' }
            });
        }
    }

    /**
     * Handle authentication state changes (login/logout)
     * This method is called from app.component.ts when authentication state changes
     */
    handleAuthenticationChange(isAuthenticated: boolean): void {
        const currentUrl = this.router.url;
        console.log('AppInitializationService: Handling authentication change. Authenticated:', isAuthenticated, 'URL:', currentUrl);
        
        if (isAuthenticated) {
            // User logged in - handle navigation if needed
            console.log('AppInitializationService: User authenticated, checking if navigation is needed');
            
            // For authenticated users, only navigate if they're on auth routes
            if (this.isAuthRoute(currentUrl) || this.isRootRoute(currentUrl)) {
                // Determine target route - avoid root paths
                let targetRoute = this.lastSuccessfulRoute;
                
                // If lastSuccessfulRoute is empty or a root route, use dashboard
                if (!targetRoute || this.isRootRoute(targetRoute) || this.isAuthRoute(targetRoute)) {
                    targetRoute = appRouteConstants.DASHBOARD;
                }
                
                console.log('AppInitializationService: User authenticated on auth/root route, navigating to:', targetRoute);
                
                // Use setTimeout to avoid navigation conflicts and ensure clean navigation
                setTimeout(() => {
                    // Navigate without any query parameters to clean the URL
                    this.router.navigate([targetRoute]).then(success => {
                        if (success) {
                            console.log('AppInitializationService: Successfully navigated to:', targetRoute);
                        } else {
                            console.error('AppInitializationService: Navigation failed to:', targetRoute);
                        }
                    });
                }, 100);
            }
        } else {
            // User logged out - reset state and don't trigger re-initialization
            console.log('AppInitializationService: User logged out, resetting state');
            this.autoRedirectDisabled = false;
            this.lastSuccessfulRoute = '';
            
            // Don't navigate here - let the logout handler in nav-bar handle the navigation
            // This prevents triggering unnecessary initialization calls
        }
    }
}